<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search invites..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchInvites"
      @page-change="goToPage"
    >
      <template #actions>
        <router-link
          to="/users"
          class="btn-secondary"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
          Users
        </router-link>
        <button
          @click="showInviteModal = true"
          class="btn-primary"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Invite User
        </button>
      </template>
    </ListToolbar>

    <!-- Table -->
    <div v-if="invites.length" class="bg-white rounded-xl border border-gray-200/60 overflow-hidden card-shadow">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Email</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Is Admin</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Expires</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Status</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-50">
          <tr v-for="invite in invites" :key="invite._id" class="hover:bg-gray-50/50 transition-colors">
            <td class="px-6 py-4">
              <p class="text-[14px] text-gray-900">{{ invite.email }}</p>
            </td>
            <td class="px-6 py-4">
              <span
                :class="[
                  'inline-flex items-center px-2.5 py-1 rounded-md text-sm font-semibold',
                  invite.is_admin
                    ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                    : 'bg-gray-100 text-gray-400'
                ]"
              >
                {{ invite.is_admin ? 'Yes' : 'No' }}
              </span>
            </td>
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="text-sm text-gray-400">{{ $filters.formatDatetime(invite.expires_at) }}</span>
            </td>
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="text-sm" :class="invite.used_at ? 'badge-live' : 'badge-idle'">
                {{ invite.used_at ? 'Used' : 'Pending' }}
              </span>
            </td>
            <td class="px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1">
                <ActionsMenu :items="inviteActions(invite)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty -->
    <div v-else class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/>
        </svg>
      </div>
      <p class="text-base font-medium text-gray-900">No invites yet</p>
      <p class="text-sm text-gray-400 mt-1">Send invitations to add team members</p>
    </div>

    <ConfirmModal
      :show="showDeleteModal"
      :loading="deleting"
      title="Delete Invite"
      :message="`Delete invite for '${deleteItem?.email}'? This cannot be undone.`"
      confirm-text="Delete"
      :danger="true"
      @confirm="deleteInvite"
      @cancel="showDeleteModal = false"
    />

    <!-- Invite modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showInviteModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">Invite User</h3>
              <button @click="closeInviteModal" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5">
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1.5">Email</label>
                  <input
                    v-model="inviteForm.email"
                    type="email"
                    required
                    placeholder="user@company.com"
                    class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]"
                  />
                </div>

                <div class="flex items-center justify-between py-1">
                  <span class="text-sm text-gray-600">Send invite email</span>
                  <ToggleSwitch v-model="inviteForm.sendInvite" />
                </div>

                <div class="flex items-center justify-between py-1">
                  <span class="text-sm text-gray-600">Admin user</span>
                  <ToggleSwitch v-model="inviteForm.is_admin" />
                </div>

                <template v-if="!inviteForm.is_admin">
                  <div class="flex items-center justify-between py-1">
                    <span class="text-sm text-gray-600">Clone permissions from existing user</span>
                    <ToggleSwitch v-model="inviteForm.clonePermissions" />
                  </div>

                <div v-if="inviteForm.clonePermissions">
                  <label class="block text-sm font-medium text-gray-600 mb-1.5">Search user</label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                      v-model="userSearch"
                      type="text"
                      placeholder="Search by name or email..."
                      class="w-full h-[42px] pl-9 pr-3 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]"
                      @input="onUserSearch"
                    />
                  </div>
                  <!-- Selected user -->
                  <div v-if="selectedCloneUser" class="flex items-center justify-between mt-2 px-3 py-2 rounded-lg bg-[var(--color-primary-light)] border border-[var(--color-primary-glow)]">
                    <div class="flex items-center gap-2 min-w-0">
                      <svg class="w-4 h-4 text-[var(--color-primary)] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      <div class="min-w-0">
                        <p class="text-sm font-medium text-[var(--color-primary)] truncate">{{ selectedCloneUser.name || selectedCloneUser.email }}</p>
                        <p v-if="selectedCloneUser.name" class="text-xs text-gray-400 truncate">{{ selectedCloneUser.email }}</p>
                      </div>
                    </div>
                    <button type="button" @click="selectedCloneUser = null; inviteForm.clone_user_id = null" class="p-1 rounded text-gray-400 hover:text-red-500 transition-colors shrink-0">
                      <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  <!-- User results -->
                  <div v-else-if="userResults.length" class="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                    <button
                      v-for="user in userResults" :key="user._id"
                      type="button"
                      @click="selectCloneUser(user)"
                      class="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                    >
                      <svg class="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      <div class="min-w-0">
                        <p class="text-sm text-gray-900 truncate">{{ user.name || user.email }}</p>
                        <p v-if="user.name" class="text-xs text-gray-400 truncate">{{ user.email }}</p>
                      </div>
                    </button>
                  </div>
                  <p v-else-if="userSearch && !searchingUsers && !selectedCloneUser" class="text-xs text-gray-400 mt-2">No users found</p>
                </div>
                </template>

                <!-- Invite URL result -->
                <div v-if="inviteUrl" class="p-3 rounded-lg bg-green-50 border border-green-100">
                  <p class="text-xs font-medium text-green-700 mb-2">Invite created! Share this link:</p>
                  <div class="flex items-center gap-2">
                    <input :value="inviteUrl" disabled class="flex-1 h-9 px-3 rounded-md border border-green-200 bg-white text-xs text-gray-700 font-data" />
                    <button
                      @click="copyInviteUrl"
                      class="h-9 px-3 rounded-md bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors shrink-0"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              </div>
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button
                  type="button"
                  @click="closeInviteModal"
                  class="px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {{ inviteUrl ? 'Done' : 'Cancel' }}
                </button>
                <button
                  v-if="!inviteUrl"
                  @click="sendInvite"
                  :disabled="!inviteForm.email || sendingInvite"
                  class="px-5 py-2.5 text-sm font-medium bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-50"
                >
                  {{ sendingInvite ? 'Sending...' : 'Send Invite' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import ConfirmModal from '../../components/ConfirmModal.vue'
import ToggleSwitch from '../../components/ToggleSwitch.vue'
import icons from '../../components/action-icons.js'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

export default {
  name: 'InvitesList',
  components: { ListToolbar, ActionsMenu, ConfirmModal, ToggleSwitch },

  data() {
    return {
      invites: [],
      search: '',
      sort: 'created:-1',
      sortOptions: [
        { value: 'created:1', label: 'Created (oldest)' },
        { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 10, page: 1, total: 0 },
      showInviteModal: false,
      sendingInvite: false,
      inviteForm: { email: '', is_admin: false, sendInvite: false, clonePermissions: false, clone_user_id: null },
      inviteUrl: null,
      userSearch: '',
      userResults: [],
      searchingUsers: false,
      selectedCloneUser: null,
      userSearchTimer: null,
      showDeleteModal: false,
      deleting: false,
      deleteItem: null
    }
  },

  watch: {
    search() { this.pagination.page = 1; this.fetchInvites() },
    sort() { this.pagination.page = 1; this.fetchInvites() },
    'inviteForm.is_admin'(val) {
      if (val) {
        this.inviteForm.clonePermissions = false
        this.inviteForm.clone_user_id = null
        this.selectedCloneUser = null
        this.userSearch = ''
        this.userResults = []
      }
    }
  },

  mounted() {
    this.fetchInvites()
  },

  methods: {
    async fetchInvites(count = true) {
      try {
        const params = { limit: this.pagination.limit, page: this.pagination.page }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (count) params.count = true
        const res = await api.get('/invites', { params })
        this.invites = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch {
        // keep existing data
      }
    },

    goToPage(page) {
      this.pagination.page = page
      this.fetchInvites(false)
    },

    async sendInvite() {
      this.sendingInvite = true
      try {
        const payload = {
          email: this.inviteForm.email,
          is_admin: this.inviteForm.is_admin,
          sendInvite: this.inviteForm.sendInvite
        }
        if (this.inviteForm.clonePermissions && this.inviteForm.clone_user_id) {
          payload.clone_user_id = this.inviteForm.clone_user_id
        }
        const res = await api.post('/invites', payload)
        this.inviteUrl = res.data?.inviteUrl
        if (!this.inviteUrl) {
          notifySuccess('Invite sent successfully')
          this.closeInviteModal()
        }
        this.fetchInvites()
      } catch (err) {
        notifyError(err?.message || 'Failed to send invite')
      } finally {
        this.sendingInvite = false
      }
    },

    closeInviteModal() {
      this.showInviteModal = false
      this.inviteForm = { email: '', is_admin: false, sendInvite: false, clonePermissions: false, clone_user_id: null }
      this.inviteUrl = null
      this.userSearch = ''
      this.userResults = []
      this.selectedCloneUser = null
    },

    inviteActions(invite) {
      return [
        { label: 'Copy Invite Link', action: () => this.copyInviteLink(invite), icon: icons.copy },
        { divider: true },
        { label: 'Delete', danger: true, action: () => this.confirmDelete(invite), icon: icons.delete }
      ]
    },

    async copyInviteLink(invite) {
      const url = `${window.location.origin}/sign-up/${invite.token}`
      await navigator.clipboard.writeText(url)
      notifySuccess('Invite link copied!')
    },

    async copyInviteUrl() {
      await navigator.clipboard.writeText(this.inviteUrl)
      notifySuccess('Invite URL copied!')
    },

    onUserSearch() {
      clearTimeout(this.userSearchTimer)
      if (!this.userSearch.trim()) {
        this.userResults = []
        return
      }
      this.userSearchTimer = setTimeout(() => this.searchUsers(), 300)
    },

    async searchUsers() {
      this.searchingUsers = true
      try {
        const res = await api.get('/users', { params: { text: this.userSearch, limit: 10 }, silent: true })
        this.userResults = res.data?.items || []
      } catch {
        this.userResults = []
      } finally {
        this.searchingUsers = false
      }
    },

    selectCloneUser(user) {
      this.selectedCloneUser = user
      this.inviteForm.clone_user_id = user._id
      this.userSearch = ''
      this.userResults = []
    },

    confirmDelete(invite) {
      this.deleteItem = invite
      this.showDeleteModal = true
    },

    async deleteInvite() {
      if (!this.deleteItem) return
      this.deleting = true
      try {
        await api.delete(`/invites/${this.deleteItem._id}`)
        notifySuccess('Invite deleted')
        this.showDeleteModal = false
        this.deleteItem = null
        this.fetchInvites()
      } catch (err) {
        notifyError(err?.message || 'Failed to delete invite')
      } finally { this.deleting = false }
    }
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
